import { View, Text } from 'tamagui'
import { HeroSelect } from '@components/HeroSelect'
import { QuestionWrapper } from './QuestionWrapper'
import { useFormContext } from 'react-hook-form'
import { IExamQuestionAssertion } from '@dtos/Activity'
import { useMemo, useEffect, useRef, useCallback } from 'react'

interface IAssociativeQuestionProps {
  firstAssertions: IExamQuestionAssertion[]
  secondAssertions: IExamQuestionAssertion[]
  questionId: string
  previousAnswer?: {
    associativeOptions: Array<{
      associativeExamQuestionId: string
      correct: boolean
      examQuestionAnswerId: string
      id: string
      firstAssertion: {
        id: string
        text: string
      }
      secondAssertion: {
        id: string
        text: string
      }
    }>
    correct: boolean
    score: number
  }
  mode: 'edit' | 'readonly' | 'retry'
}

export function AssociativeQuestion({
  firstAssertions,
  secondAssertions,
  questionId,
  previousAnswer,
  mode,
}: IAssociativeQuestionProps) {
  const { watch, setValue, getValues } = useFormContext()
  const hasInitialized = useRef(false)

  // Observa os valores selecionados para esta questão
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedValues = watch(questionId) || {}

  // Cria opções para o select baseado nas firstAssertions
  const selectOptions = useMemo(() => {
    const options = firstAssertions.map((assertion, index) => ({
      name: `${index + 1}`,
      value: assertion.id,
    }))
    return options
  }, [firstAssertions])

  // Função para obter a cor baseada no estado da resposta (similar ao TrueFalseQuestion)
  const getColor = useCallback(
    (secondAssertionId: string, isTransparent = false) => {
      const selectedValue = selectedValues[secondAssertionId]

      if (selectedValue) {
        if (!previousAnswer) return '$primary-300'

        // Encontra a associativeOption correspondente
        const associativeOption = previousAnswer.associativeOptions?.find(
          (option) =>
            option.firstAssertion.id === selectedValue &&
            option.secondAssertion.id === secondAssertionId,
        )

        if (associativeOption?.correct) return '$success-300'
        return '$danger-300'
      }

      return isTransparent ? 'transparent' : '$neutral-300'
    },
    [selectedValues, previousAnswer],
  )

  // Popula valores iniciais quando há previousAnswer
  useEffect(() => {
    if (previousAnswer?.associativeOptions && !hasInitialized.current) {
      const initialValues: { [key: string]: string } = {}

      previousAnswer.associativeOptions.forEach((option) => {
        const firstAssertion = option.firstAssertion
        const secondAssertion = option.secondAssertion

        if (firstAssertion && secondAssertion) {
          initialValues[secondAssertion.id] = firstAssertion.id
        }
      })

      if (Object.keys(initialValues).length > 0) {
        setValue(questionId, initialValues, { shouldValidate: false })
        hasInitialized.current = true
      }
    }
  }, [previousAnswer, questionId, setValue, watch, secondAssertions])

  // Função para lidar com mudanças nas seleções
  const handleSelectionChange = (
    secondAssertionId: string,
    selectedValue: string,
  ) => {
    // Só permite mudanças se estiver em modo edit
    if (mode !== 'edit') return

    const currentValues = getValues(questionId) || {}
    const selectedOption = selectOptions.find(
      (opt) => opt.name === selectedValue,
    )

    const updatedValues = {
      ...currentValues,
      [secondAssertionId]: selectedOption?.value || '',
    }
    setValue(questionId, updatedValues, { shouldValidate: true })
  }

  // Função para obter o valor de exibição
  const getDisplayValue = (secondAssertionId: string) => {
    const selectedValue = selectedValues[secondAssertionId]
    const selectedOption = selectOptions.find(
      (opt) => opt.value === selectedValue,
    )
    return selectedOption?.name || ''
  }

  return (
    <View gap="$spacing-inset-xs">
      {/* Primeira coluna - Statements */}
      <View gap="$spacing-inset-xs">
        {firstAssertions.map((assertion, index) => (
          <View flexDirection="row" gap="$spacing-inset-xs" key={assertion.id}>
            <Text
              alignSelf="center"
              fontFamily="$mulishRegular"
              fontSize="$font-size-sm"
              color="$neutral-800"
              lineHeight={21}
            >
              {index + 1}.
            </Text>
            <View
              borderWidth={1}
              borderColor="$neutral-100"
              flex={1}
              p="$spacing-inset-xs"
              borderRadius="$border-radius-sm"
            >
              <Text
                fontFamily="$mulishRegular"
                fontSize="$font-size-xxs"
                color="$neutral-800"
                lineHeight={21}
              >
                {assertion.text}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Segunda coluna - Answers com selects */}
      <View gap="$spacing-inset-xs" flex={1}>
        {secondAssertions.map((assertion) => (
          <QuestionWrapper key={assertion.id} color={getColor(assertion.id)}>
            <View
              flexDirection="row"
              py="$spacing-inset-nano"
              gap="$spacing-inset-xs"
              pr="$spacing-inset-xs"
            >
              <View maxWidth={72} alignSelf="center">
                <HeroSelect
                  value={getDisplayValue(assertion.id)}
                  onValueChange={(value) =>
                    handleSelectionChange(assertion.id, value)
                  }
                  placeholder="N"
                  options={selectOptions}
                />
              </View>

              <View flex={1} alignItems="flex-start" justifyContent="center">
                <Text
                  color="$neutral-700"
                  fontSize="$font-size-xxs"
                  fontFamily="$mulishRegular"
                  lineHeight={21}
                >
                  {assertion.text}
                </Text>
              </View>
            </View>
          </QuestionWrapper>
        ))}
      </View>
    </View>
  )
}
